export const PERSON_LOOKUP_PRIMARY_CRITERIA = ['birth_date', 'gender'];
export const PERSON_LOOKUP_ADDRESS_CRITERIA = [
    ...PERSON_LOOKUP_PRIMARY_CRITERIA,
    'postal_code',
    'house_number',
];
export const PERSON_LOOKUP_BSN_PATTERN = /^\d{9}$/;
export const PERSON_LOOKUP_POSTAL_CODE_PATTERN = /^[1-9]\d{3}\s?[a-zA-Z]{2}$/;
export const PERSON_LOOKUP_HOUSE_NUMBER_PATTERN = /^[1-9]\d*$/;

function hasCriteriaValue(criteria, field) {
    return String(criteria[field] ?? '').trim() !== '';
}

function isValidBirthDate(value) {
    const match = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (!match) {
        return false;
    }

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));

    return date.getFullYear() === Number(match[1]) &&
        date.getMonth() === Number(match[2]) - 1 &&
        date.getDate() === Number(match[3]);
}

export function isValidPersonLookupBsn(value) {
    return PERSON_LOOKUP_BSN_PATTERN.test(String(value ?? '').trim());
}

export function isValidPersonLookupBirthDate(value) {
    return isValidBirthDate(value);
}

export function isValidPersonLookupPostalCode(value) {
    return PERSON_LOOKUP_POSTAL_CODE_PATTERN.test(String(value ?? '').trim());
}

export function isValidPersonLookupHouseNumber(value) {
    return PERSON_LOOKUP_HOUSE_NUMBER_PATTERN.test(String(value ?? '').trim());
}

export function hasCompletePersonLookupPrimaryCriteria(criteria) {
    return isValidPersonLookupBirthDate(criteria.birth_date) &&
        ['male', 'female', 1, 2, '1', '2'].includes(criteria.gender);
}

export function hasCompletePersonLookupNamePath(criteria) {
    return hasCompletePersonLookupPrimaryCriteria(criteria) && hasCriteriaValue(criteria, 'last_name');
}

export function hasCompletePersonLookupAddressPath(criteria, countryNetherlandsId = null) {
    const hasDutchAddress = countryNetherlandsId === null ||
        String(criteria.country_code_id || '') === String(countryNetherlandsId);

    return hasDutchAddress &&
        hasCompletePersonLookupPrimaryCriteria(criteria) &&
        isValidPersonLookupPostalCode(criteria.postal_code) &&
        isValidPersonLookupHouseNumber(criteria.house_number);
}

export function hasCompletePersonLookupCriteria(criteria, countryNetherlandsId = null) {
    if (hasCriteriaValue(criteria, 'identification_number')) {
        return isValidPersonLookupBsn(criteria.identification_number);
    }

    return hasCompletePersonLookupNamePath(criteria) ||
        hasCompletePersonLookupAddressPath(criteria, countryNetherlandsId);
}

export function personLookupCriteriaFeedback(criteria, countryNetherlandsId = null) {
    if (hasCriteriaValue(criteria, 'identification_number')) {
        return isValidPersonLookupBsn(criteria.identification_number)
            ? null
            : { type: 'invalid_bsn', fields: ['identification_number'], missingFields: [] };
    }

    if (hasCriteriaValue(criteria, 'birth_date') && !isValidPersonLookupBirthDate(criteria.birth_date)) {
        return { type: 'invalid_birth_date', fields: ['birth_date'], missingFields: [] };
    }

    const missingPrimaryFields = PERSON_LOOKUP_PRIMARY_CRITERIA
        .filter(field => !hasCriteriaValue(criteria, field) ||
            (field === 'gender' && !['male', 'female', 1, 2, '1', '2'].includes(criteria.gender)));

    if (missingPrimaryFields.length > 0) {
        return {
            type: missingPrimaryFields.length === PERSON_LOOKUP_PRIMARY_CRITERIA.length ? 'empty' : 'primary',
            fields: PERSON_LOOKUP_PRIMARY_CRITERIA,
            missingFields: missingPrimaryFields,
        };
    }

    if (hasCompletePersonLookupCriteria(criteria, countryNetherlandsId)) {
        return null;
    }

    const hasAddressInput = ['postal_code', 'house_number'].some(field => hasCriteriaValue(criteria, field));

    if (hasAddressInput) {
        const invalidFields = [];

        if (!isValidPersonLookupPostalCode(criteria.postal_code)) {
            invalidFields.push('postal_code');
        }
        if (!isValidPersonLookupHouseNumber(criteria.house_number)) {
            invalidFields.push('house_number');
        }

        return { type: 'address', fields: PERSON_LOOKUP_ADDRESS_CRITERIA, missingFields: invalidFields };
    }

    return { type: 'secondary', fields: ['last_name', 'postal_code', 'house_number'], missingFields: [] };
}

export const PERSON_LOOKUP_RESPONSE_TARGETS = [
    { group: 'person', targetPath: 'formData' },
    { group: 'name', targetPath: 'formData.name' },
    { group: 'address', targetPath: 'formData.address' },
];

export const PERSON_LOOKUP_RESULT_GROUPS = ['person', 'name', 'address', 'insurance'];

export const PERSON_LOOKUP_FIELDS = [
    {
        field: 'identification_number',
        path: 'identification_number',
        criteriaKey: 'identification_number',
        invalidatesPolicies: true,
    },
    { field: 'deceased', path: 'deceased', type: 'boolean' },
    { field: 'deceased_date', path: 'deceased_date', type: 'date' },
    { field: 'birthdate', path: 'birthdate', criteriaKey: 'birth_date', invalidatesPolicies: true, type: 'date' },
    { field: 'gender', path: 'gender', criteriaKey: 'gender', invalidatesPolicies: true },
    { field: 'lastname', path: 'name.lastname', criteriaKey: 'last_name', invalidatesPolicies: true },
    { field: 'lastname_prefix', path: 'name.lastname_prefix' },
    { field: 'initials', path: 'name.initials' },
    { field: 'firstname', path: 'name.firstname' },
    { field: 'call_name', path: 'name.call_name' },
    { field: 'street', path: 'address.street' },
    { field: 'house_number', path: 'address.house_number', criteriaKey: 'house_number', invalidatesPolicies: true },
    {
        field: 'house_number_suffix',
        path: 'address.house_number_suffix',
        criteriaKey: 'house_number_addition',
        invalidatesPolicies: true,
    },
    {
        field: 'postal_code',
        path: 'address.postal_code',
        criteriaKey: 'postal_code',
        invalidatesPolicies: true,
        type: 'postal_code',
    },
    { field: 'city', path: 'address.city' },
    {
        field: 'country_id',
        path: 'address.country_id',
        criteriaKey: 'country_code_id',
        invalidatesPolicies: true,
    },
];

export const PERSON_LOOKUP_POLICY_FIELDS = PERSON_LOOKUP_FIELDS
    .filter(f => f.invalidatesPolicies)
    .reduce((acc, f) => ({ ...acc, [f.path]: f }), {});

export const PERSON_LOOKUP_FIELDS_BY_CRITERIA_KEY = PERSON_LOOKUP_FIELDS
    .filter(f => f.criteriaKey)
    .reduce((acc, f) => ({ ...acc, [f.criteriaKey]: f }), {});

export const PERSON_LOOKUP_FIELDS_BY_FIELD = PERSON_LOOKUP_FIELDS
    .reduce((acc, f) => ({ ...acc, [f.field]: f }), {});

/**
 * Normalise a person-lookup value for stable comparison.
 * Dates are truncated to YYYY-MM-DD; postal codes are uppercased without spaces.
 *
 * @param {*}           value
 * @param {string|null} type
 * @returns {string}
 */
export function normalizePersonLookupValue(value, type = null) {
    const normalized = String(value ?? '').trim();

    if (type === 'date') {
        return normalized.substr(0, 10);
    }

    return type === 'postal_code' ? normalized.replace(/\s+/g, '').toUpperCase() : normalized;
}
